import React, { useState, useEffect } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import {
    Flex,
    Box,
    Text,
    Badge,
} from "@chakra-ui/react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import {
    EllipsisVertical,
    Plus,
    Pencil,
    Archive,
    EyeIcon,
} from "lucide-react";
import GradientButton from "components/Buttons/GradientButton";
import ViewBusiness from "./components/ViewBusiness";
import EditBusinessDrawer from "./components/EditBusinessDrawer";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBusinessesAction } from '../../../state/actions/businessActions';
import { formatDate } from "utils/dateFormatter";
import { useCustomNotification } from 'components/Toasts/OpenNotification.js';
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

const BusinessManagement = () => {
    const [isAddBusinessDrawerOpen, setAddBusinessDrawerOpen] = useState(false);
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [isViewBusinessDrawerOpen, setViewBusinessDrawerOpen] = useState(false);
    const [isEditBusinessDrawerOpen, setEditBusinessDrawerOpen] = useState(false);
    const history = useHistory();
    const showToast = useCustomNotification();
    const dispatch = useDispatch();
    const { businesses, loading, error } = useSelector((state) => state.businesses);

    useEffect(() => {
        dispatch(fetchBusinessesAction());  // Fetch businesses on mount
    }, [dispatch]);

    const handleViewBusiness = (business) => {
        setSelectedBusiness(business);
        setViewBusinessDrawerOpen(true);
    };

    const handleCloseViewBusinessDrawer = () => {
        setSelectedBusiness(null);
        setViewBusinessDrawerOpen(false);
    };

    const handleOpenEditBusinessDrawer = (business) => {
        setSelectedBusiness(business);
        setEditBusinessDrawerOpen(true);
    };

    const handleCloseEditBusinessDrawer = () => {
        setSelectedBusiness(null);
        setEditBusinessDrawerOpen(false);
    };

    const generateMenuItems = (row) => [
        {
            icon: <EyeIcon size={16} />,
            text: "View Business",
            onClick: () => handleViewBusiness(row.original),
        },
        {
            icon: <Pencil size={16} />,
            text: "Update Business Status",
            onClick: () => handleOpenEditBusinessDrawer(row.original),
        },
    ];

    const statusColorScheme = {
        Approved: "green",
        Pending: "yellow",
        Suspended: "red",
        Rejected: "red",
        Deactivated: "gray",
    };

    const columnsData = [
        {
            Header: "Business Name",
            accessor: "name_of_company",
        },
        {
            Header: "CIPA Number",
            accessor: "cipa_number",
        },
        {
            Header: "Place of Operation",
            accessor: "place_of_operation",
        },
       {
    Header: "Status",
    accessor: (row) =>
        row.status || "Unknown",

    Cell: ({ value }) => (
        <StatusBadge
            label={value}
            variant={getStatusVariant(value)}
        />
    ),
},
        {
            Header: "Date of Incorporation",
            accessor: "date_of_incorporation",
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: "Actions",
            accessor: "moreOptions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={<EllipsisVertical size={20} color="grey" />}
                    items={generateMenuItems(row)}
                />
            ),
        },
    ];

    return (
        <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
            <Box p={6}>
                <Flex justifyContent="space-between" alignItems="center">
                    <Text fontSize="md" fontWeight="bold">
                        {loading ? (
                            <Skeleton height="20px" width="100px" />
                        ) : (
                            `All Businesses: ${businesses?.total ?? 0}`
                        )}
                    </Text>
                    {/* <GradientButton
                        icon={<Plus size={16} />}
                        onClick={() => setAddBusinessDrawerOpen(true)}
                        height={"40px"}
                    >
                        Add Business
                    </GradientButton> */}
                </Flex>
            </Box>
            <ReusableTable
                columnsData={columnsData}
                tableData={Array.isArray(businesses?.data) ? businesses.data : []}
                isLoading={loading}
            />

            {/* View Business Drawer */}
            <ReusableDrawer
                isOpen={isViewBusinessDrawerOpen}
                onClose={handleCloseViewBusinessDrawer}
                headerContent={<h2 style={{ fontSize: "md" }}>View Business</h2>}
                bodyContent={
                    selectedBusiness && (
                        <ViewBusiness
                            business={selectedBusiness}
                            onClose={handleCloseViewBusinessDrawer}
                        />
                    )
                }
            />

            <EditBusinessDrawer
                isOpen={isEditBusinessDrawerOpen}
                onClose={handleCloseEditBusinessDrawer}
                business={selectedBusiness}
            />
        </Flex>
    );
};

export default BusinessManagement;
